<template>
  <div :id="header.id" class="sideBar">
    <v-navigation-drawer
      id="sidebar"
      v-model="drawer"
      permanent
      class="itemis-analyze-shadow"
      :rail="rail"
    >
      <v-divider></v-divider>

      <v-list density="compact" nav active-class="active-sidebar-item" open-strategy="multiple">
        <template v-if="rail">
          <div v-for="(item, i) in items" :key="i">
            <v-list-item
              v-if="item.type === 'simpleitem' || item.type === 'configurationItem'"
              :id="`sidebar-item-${item.id}`"
              link
              class="sidebar-item"
              :to="item.to"
              :prepend-icon="item.icon"
              :disabled="item.disabled"
              :value="item"
              @click="item.id === labels.SIDEBAR.MAIN.DASHBOARDS_ID ? toggleSidebar() : () => {}"
            >
              <v-list-item-title
                :class="item.disabled ? 'v-list-item-title-disabled' : 'sidebar-item-title'"
              >
                {{ item.title }}
              </v-list-item-title>
              <v-tooltip class="custom-tooltip" activator="parent">
                {{ item.title }}
              </v-tooltip>
            </v-list-item>
          </div>
        </template>

        <template v-if="!rail">
          <v-list-item></v-list-item>
          <div v-for="(item, i) in items" :key="i">
            <v-list-item
              v-if="item.type === 'simpleitem' && item.id !== labels.SIDEBAR.MAIN.DASHBOARDS_ID"
              :id="`sidebar-item-${item.id}`"
              link
              class="sidebar-item mb-2"
              :to="item.to"
              :prepend-icon="item.icon"
              :disabled="item.disabled"
              :value="item"
            >
              <v-list-item-title
                :class="
                  item.disabled ? 'v-list-item-title-disabled pl-8' : 'sidebar-item-title pl-8'
                "
              >
                {{ item.title }}
              </v-list-item-title>
              <v-tooltip class="custom-tooltip" activator="parent">
                {{ item.title }}
              </v-tooltip>
            </v-list-item>

            <div v-if="item.type === 'dashboarditem'">
              <v-list-group
                id="sidebar-item-dashboards"
                :prepend-icon="CONSTANTS.ICONS_MDI.ICON_CHART_BAR"
              >
                <template #activator="{ isOpen, props }">
                  <v-list-item v-bind="props" @click="setOpenStateForDashboard(isOpen)">
                    <v-list-item-title>
                      <span class="itemis-analyze-title pl-8">
                        {{ labels.DASHBOARD.DASHBOARDS }}
                      </span>
                    </v-list-item-title>
                  </v-list-item>
                </template>

                <v-row class="d-flex pt-2 pb-4">
                  <v-col class="pr-0">
                    <v-divider
                      v-if="!rail"
                      :thickness="2"
                      class="border-opacity-100 pr-0 mr-0 divider-style"
                    />
                  </v-col>
                  <v-btn
                    :icon="CONSTANTS.ICONS_MDI.ICON_PLUS"
                    size="x-small"
                    :class="!rail ? 'add-dashboard-btn' : 'add-dashboard-btn mr-5'"
                    variant="tonal"
                    @click="addNewDashboard"
                  />
                </v-row>

                <v-list-item
                  v-if="dashboardItems.length === 0"
                  id="sidebar-item-dashboard-loader"
                  :prepend-icon="CONSTANTS.ICONS_MDI.ICON_CHART_BAR"
                >
                  <v-list-item-title>
                    <LoaderCircular :loader-size="25" :loader-width="4" />
                  </v-list-item-title>
                </v-list-item>

                <template v-for="dashboardItem in item.dashboards" :key="dashboardItem.id">
                  <v-hover v-slot="{ isHovering, props }">
                    <v-list-item
                      :id="`sidebar-item-dashboard-${dashboardItem.id}`"
                      v-bind="props"
                      link
                      data-testid="sidebar-item-dashboard"
                      class="sidebar-item"
                      :to="dashboardItem.to"
                      :disabled="dashboardItem.disabled"
                      :value="dashboardItem"
                    >
                      <v-list-item-title
                        :class="
                          dashboardItem.disabled
                            ? 'v-list-item-title-disabled'
                            : 'sidebar-item-title'
                        "
                      >
                        {{ dashboardItem.title }}
                      </v-list-item-title>

                      <template v-if="isHovering && !isLastDashboard" #append>
                        <v-btn
                          :id="`delete-dashboard-btn-${dashboardItem.id}`"
                          data-testid="delete-dashboard-button"
                          density="compact"
                          variant="text"
                          :icon="CONSTANTS.ICONS_MDI.ICON_DELETE_OUTLINE"
                          @click.prevent="setDashboardToBeDeleted(dashboardItem.id)"
                        >
                        </v-btn>
                      </template>

                      <v-tooltip v-if="rail" class="custom-tooltip" activator="parent">
                        {{ dashboardItem.title }}
                      </v-tooltip>
                    </v-list-item>
                  </v-hover>
                </template>

                <ConfirmDeletionDialog
                  v-if="dashboardToBeDeleted"
                  :key="`dialog-${dashboardToBeDeleted?.id}`"
                  :element-to-delete="dashboardToBeDeleted"
                  :is-deleting-element="isDeletingDashboard"
                  :get-element-name="
                    (d?: DashboardResource) => d?.name ?? labels.DASHBOARD.DEFAULT_DASHBOARD_TITLE
                  "
                  :element-type-name="labels.DELETE_ELEMENT.DASHBOARD"
                  @confirm-deletion="removeDashboard($event)"
                  @cancel-deletion="dashboardToBeDeleted = undefined"
                />
              </v-list-group>
              <v-divider v-if="!rail" class="border-opacity-50 pt-2 pb-0" />
            </div>

            <div v-if="item.type === 'configurationItem'">
              <v-list-item></v-list-item>
              <v-list-group
                id="sidebar-item-configuration"
                :prepend-icon="CONSTANTS.ICONS_MDI.ICON_CONFIG"
              >
                <template #activator="{ isOpen, props }">
                  <v-list-item
                    id="sidebar-config-head"
                    v-bind="props"
                    @click="setOpenStateForConfiguration(isOpen)"
                  >
                    <v-list-item-title>
                      <span class="itemis-analyze-title pl-8">
                        {{ labels.SIDEBAR.MAIN.PROJECT_CONFIGURATION }}
                      </span>
                    </v-list-item-title>
                  </v-list-item>
                </template>

                <template
                  v-for="configurationItem in configurationItems"
                  :key="configurationItem.id"
                >
                  <v-hover v-slot="{ props }">
                    <v-list-item
                      :id="`sidebar-item-dashboard-${configurationItem.id}`"
                      v-bind="props"
                      link
                      data-testid="sidebar-item-dashboard"
                      class="sidebar-item"
                      :to="configurationItem.to"
                      :disabled="configurationItem.disabled"
                      :value="configurationItem"
                    >
                      <v-list-item-title
                        :class="
                          configurationItem.disabled
                            ? 'v-list-item-title-disabled'
                            : 'sidebar-item-title'
                        "
                      >
                        {{ configurationItem.title }}
                      </v-list-item-title>

                      <v-tooltip v-if="rail" class="custom-tooltip" activator="parent">
                        {{ configurationItem.title }}
                      </v-tooltip>
                    </v-list-item>
                  </v-hover>
                </template>
              </v-list-group>
            </div>
          </div>
        </template>
      </v-list>

      <template #append>
        <div class="pa-2">
          <v-btn
            v-if="!rail"
            class="text-none collapse-btn"
            size="large"
            variant="text"
            block
            :prepend-icon="CONSTANTS.ICONS_MDI.ICON_CHEVRON_LEFT"
            @click="toggleSidebar"
          >
            <template #prepend>
              <v-icon class="pr-5 collapse-icon" size="large" />
            </template>

            {{ labels.SIDEBAR.TOGGLE_SIDEBAR_BUTTON.COLLAPSE }}
          </v-btn>

          <v-btn-group v-if="rail" class="expand-btn-group" variant="outlined">
            <v-btn
              :icon="CONSTANTS.ICONS_MDI.ICON_CHEVRON_RIGHT"
              block
              class="expand-btn"
              @click="toggleSidebar"
            />
          </v-btn-group>
        </div>
      </template>
    </v-navigation-drawer>

    <v-main></v-main>
  </div>
</template>

<script lang="ts" setup>
  import * as HELPERS from '@/utils/helpers'
  import notifMessage from '@/translations/notifMessage.json'
  import CONSTANTS from '@/constants/Config.constants'
  import lbls from '@/translations/labels.json'
  import { computed, ref, watch, Ref } from 'vue'
  import { useSidebarStore } from '@/stores/sidebarStore'
  import { EventBus } from '@/event-bus'
  import { useRouteStore } from '@/stores/routeStore'
  import { useDashboardStore } from '@/stores/dashboardStore'
  import { useRouter } from 'vue-router'
  import { useSnapshotStore } from '@/stores/snapshotStore'
  import { storeToRefs } from 'pinia'
  import { useProjectStore } from '@/stores/projectStore'
  import { Project } from '@/models'
  import LoaderCircular from '@/components/Loader/LoaderCircular/LoaderCircular.vue'
  import ConfirmDeletionDialog from '@/components/ConfirmDeletionDialog/ConfirmDeletionDialog.vue'
  import { DashboardResource } from '@/models/dashboard.model'
  import { injectTyped } from '@/inject'
  import { ServiceKeys } from '@/services/symbols'

  const router = useRouter()
  const routeStore = useRouteStore()
  const sidebarStore = useSidebarStore()
  const dashboardStore = useDashboardStore()
  const snapshotStore = useSnapshotStore()
  const projectStore = useProjectStore()
  const labels = lbls

  const dashboardService = injectTyped(ServiceKeys.dashboard)

  const navigationMap = new Map<string, string>([
    ['explorer', 'Data Explorer'],
    ['dashboard', 'Dashboard'],
    ['configuration', 'Project Configuration'],
    ['filters', 'Global Filters'],
    ['metrics', 'Custom Metrics'],
    ['snapshots', 'Snapshot Management'],
  ])

  const header = ref(sidebarStore.getHeader)
  const drawer = ref(true)
  const isDashboardGroupOpened = ref(false)
  const isConfigurationGroupOpened = ref(false)

  const dashboardToBeDeleted: Ref<DashboardResource | undefined> = ref(undefined)
  const isDeletingDashboard: Ref<boolean> = ref(false)
  const isLastDashboard = computed(() => dashboardStore.dashboardStates.length === 1)

  const {
    getItems: items,
    getDashboardItems: dashboardItems,
    getConfigurationItems: configurationItems,
    railState: rail,
  } = storeToRefs(sidebarStore)

  const { currentSnapshot } = storeToRefs(snapshotStore)

  const currentPath = computed((): string => {
    let path = ''

    Array.from(navigationMap.keys()).forEach((key) => {
      if (routeStore.path?.includes(`/${key}`)) {
        path = key
      }
    })

    return path
  })

  watch(
    () => projectStore.currentProject,
    (newProject?: Project, oldProject?: Project) => {
      if (newProject && newProject !== oldProject)
        dashboardStore.initializeDashboards({ dashboardService, projectId: newProject.id })
    },
    { immediate: true },
  )

  watch(currentPath, (newPath, oldPath) => {
    if (oldPath === 'dashboard' && newPath === 'explorer') {
      const activeItems = document.querySelectorAll('a.v-list-item--active')

      activeItems.forEach((activeItem) => {
        if (activeItem.attributes.getNamedItem('href')?.nodeValue?.includes('/dashboard')) {
          activeItem.classList.remove('v-list-item--active')
        }
      })
    } else if (oldPath !== 'configuration' && newPath === 'configuration') {
      const activeItems = document.querySelectorAll('a.v-list-item--active')
      activeItems.forEach((activeItem) => {
        if (!activeItem.attributes.getNamedItem('href')?.nodeValue?.includes('/configuration')) {
          activeItem.classList.remove('v-list-item--active')
        }
      })
    }
  })

  function toggleSidebar(): void {
    sidebarStore.toggleRailState()

    // the size of the sidebar is inverted (collapsed = expanded and vice versa)
    let sidebarOffsetWidth = document.getElementById('sidebar')?.offsetWidth || 0

    // hence, this if statement is needed
    if (sidebarOffsetWidth === 56) {
      sidebarOffsetWidth = 256
    } else if (sidebarOffsetWidth === 256) {
      sidebarOffsetWidth = 56
    }

    EventBus.emit('sidebarToggled', sidebarOffsetWidth)
  }

  async function addNewDashboard(): Promise<void> {
    if (!routeStore.projectId) return

    const result = await dashboardStore.addNewDashboard(
      dashboardService,
      parseInt(routeStore.projectId),
    )

    router.push({
      name: labels.SIDEBAR.MAIN.DASHBOARD,
      params: {
        snapshotId: currentSnapshot.value?.id.toString(),
        dashboardId: result.dashboard.id,
      },
    })
  }

  async function removeDashboard(dashboard: DashboardResource): Promise<void> {
    if (!projectStore.currentProject) return

    if (!dashboard) return

    isDeletingDashboard.value = true

    await dashboardStore.removeDashboard(
      dashboardService,
      projectStore.currentProject.id,
      dashboard,
    )

    dashboardToBeDeleted.value = undefined

    isDeletingDashboard.value = false

    HELPERS.showTopAlertMessage(true, 'success', notifMessage.SUCCESS_ALERTS.DASHBOARD_DELETED)
  }

  function setDashboardToBeDeleted(dashboardId: string): void {
    dashboardToBeDeleted.value = dashboardStore.dashboardStates.find(
      (s) => s.dashboard.id === parseInt(dashboardId),
    )?.dashboard
  }

  function setOpenStateForDashboard(isOpen: boolean): void {
    isDashboardGroupOpened.value = !isOpen
  }
  function setOpenStateForConfiguration(isOpen: boolean): void {
    isConfigurationGroupOpened.value = !isOpen
    router.push({
      name: labels.SIDEBAR.MAIN.PROJECT_CONFIGURATION,
    })
  }
</script>

<style lang="scss">
  .v-list-item__prepend > .v-badge ~ .v-list-item__spacer,
  .v-list-item__prepend > .v-icon ~ .v-list-item__spacer,
  .v-list-item__prepend > .v-tooltip ~ .v-list-item__spacer {
    width: 0;
  }

  .v-list-item__append > .v-badge ~ .v-list-item__spacer,
  .v-list-item__append > .v-icon ~ .v-list-item__spacer,
  .v-list-item__append > .v-tooltip ~ .v-list-item__spacer {
    width: 0px;
  }
</style>

<style lang="scss" scoped>
  @use '../../assets/styles/variables';

  a.px-2.v-list-item--active.v-list-item.v-list-item--link.theme--light {
    color: transparent;
  }

  .sideBar {
    .v-list-item-title {
      color: rgb(var(--v-theme-font-color));
    }

    .v-list-item-title-disabled {
      color: rgb(var(--v-theme-font-color));
    }
  }

  .Sidebar_header_subtitle {
    font-size: smaller;
  }

  .expand-btn-group {
    width: 40px;
    height: 40px;
    border-color: rgb(var(--v-theme-grey-dark));
    border-radius: 5px;
    border-width: 1px;
  }

  .expand-btn {
    color: rgb(var(--v-theme-grey-lighty));

    &:hover {
      color: rgb(var(--v-theme-font-color));
    }
  }

  .collapse-icon {
    color: rgb(var(--v-theme-grey-dark));
  }

  .sidebar-item {
    &:hover {
      background-color: rgb(var(--v-theme-itemis-grey-light));

      .sidebar-item-title {
        color: rgb(var(--v-theme-font-color));
      }
    }
  }

  .active-sidebar-item {
    background-color: rgb(var(--v-theme-itemis-grey-light));

    .sidebar-item-title {
      color: rgb(var(--v-theme-font-color));
    }
  }

  .custom-tooltip :deep(.v-overlay__content) {
    background: rgb(var(--v-theme-grey-light));
    color: rgb(var(--v-theme-font-color));
  }

  .divider-style {
    color: rgb(var(--v-theme-itemis-analyze-primary));
  }

  .add-dashboard-btn {
    background-color: rgb(var(--v-theme-itemis-analyze-primary));
    color: rgb(var(--v-theme-surface));
    max-width: 20px;
    max-height: 20px;
    margin-right: 15px;
    margin-top: 3px;
  }

  div.v-expansion-panel-text.dashboard-expansion-panel-text
    :deep(div.v-expansion-panel-text__wrapper) {
    padding-right: 0px !important;
    padding-left: 0px !important;
    padding-bottom: 0px !important;
  }

  .dashboard-expansion-panel-title {
    padding-top: 12px;
    padding-bottom: 12px;
  }

  button.v-expansion-panel-title.v-expansion-panel-title--active.dashboard-expansion-panel-title {
    min-height: 48px;
  }

  .v-list-group__items .v-list-item {
    padding-left: 65px !important;
  }
</style>
